import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { formatAmount, formatDate, getSanitizedEnumValue } from '../../../utils/commonUtils';
import { IDisplayItems, WaiveFeeType } from '../interfaces/EmiSchedule.type';
import styles from '../components/EmiSchedule/index.module.scss';
import { ColDefsType } from '@navi/web-ui/lib/components/AgTable/types';
import { SelectPickerOptionProps } from '@navi/web-ui/lib/components/SelectPicker/types';
import { GLOBAL } from 'src/constants/Global';
import { IHeaderParams } from 'ag-grid-community';
import '../components/EmiSchedule/index.module.scss';
import EllipsisText from '@cp/src/components/EllipsisText';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { NaviTableColumnDef } from '@cp/src/components/NaviTable/interfaces';
import { Due } from '../interfaces/CaseDetail.type';

export const EMI_SCHEDULE_PARAM_KEYS = {
  EMI_CALENDAR: 'emiCalendar'
};

export const EMI_CALENDAR_MESSAGES_MAP = {
  NO_EMI_FOUND: 'No EMI found'
};

const columnSequenceGetter = (params: any) => {
  return params.node.rowIndex + 1;
};

export const CustomHeader = (props: IHeaderParams) => {
  return (
    <div>
      <div className="customHeaderLabel">{props.displayName}</div>
    </div>
  );
};

export const EmiCalendarColumnDefCallBack = (): NaviTableColumnDef<Due>[] => [
  {
    accessorKey: 'emiNumber',
    header: 'EMI no.',
    cell: info => {
      const emiNumber = info.row.original?.emiNumber;
      if (emiNumber) {
        return emiNumber;
      }
      return '--';
    },
    size: 60
  },
  {
    accessorKey: 'dueDate',
    header: 'Due Date',
    cell: info => {
      const value = info.row.original?.dueDate;
      return formatDate(value, {
        year: 'numeric'
      });
    },
    size: 100
  },
  {
    accessorKey: 'lastPaidDate',
    header: 'Actual Payment Date',
    cell: info => {
      const lastPaidDate = info.row.original?.lastPaidDate;
      const endDate = info.row.original?.endDate;
      const type = info.row.original?.type;
      if (lastPaidDate) {
        return formatDate(lastPaidDate, {
          year: 'numeric'
        });
      }
      if (endDate && type === STATUS.PART_PRE_PAYMENT) {
        return formatDate(endDate, {
          year: 'numeric'
        });
      }
      return '--';
    },
    size: 120
  },
  {
    accessorKey: 'totalAmount',
    header: 'Amount',
    cell: info => {
      const type = info.row.original?.type;
      const totalAmount = info.row.original?.totalAmount;
      if (type === STATUS.PART_PRE_PAYMENT) return '--';
      return formatAmount(totalAmount, false);
    },
    size: 80
  },
  {
    accessorKey: 'unpaid',
    header: 'Unpaid',
    cell: info => {
      const unpaidTotalAmount = info.row.original?.unpaidTotalAmount;
      const type = info.row.original?.type;
      if (type === STATUS.PART_PRE_PAYMENT) return '--';
      if (unpaidTotalAmount)
        return (
          <span className={styles['emiCalendar__colDefs__unPaidRenderer']}>
            {formatAmount(unpaidTotalAmount, false)}
          </span>
        );
      return 0;
    },
    size: 80
  },
  {
    accessorKey: 'paid',
    header: 'Paid',
    cell: info => {
      if (info.row.original?.paidAmount)
        return <span>{formatAmount(info.row.original?.paidAmount, false)}</span>;
      return 0;
    },
    size: 80
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: info => {
      const status = info.row.original?.status;
      const type = info.row.original?.type;
      if (status === STATUS.UNPAID)
        return <span className={styles['emiCalendar__colDefs__unPaidRenderer']}>Overdue</span>;
      if (status === STATUS.PAID && type === STATUS.PART_PRE_PAYMENT)
        return (
          <span className={styles['emiCalendar__colDefs__paidRenderer']}>Part Prepayment</span>
        );
      if (status === STATUS.PAID)
        return <span className={styles['emiCalendar__colDefs__paidRenderer']}>Paid</span>;
      if (status === STATUS.PARTIALLY_PAID)
        return (
          <span className={styles['emiCalendar__colDefs__partiallyPaidRenderer']}>
            Partially Paid
          </span>
        );
      if (status === STATUS.FORECLOSED)
        return (
          <span className={styles['emiCalendar__colDefs__foreclosedRenderer']}>Foreclosed</span>
        );
      return 'Scheduled';
    },
    size: 120
  },
  {
    accessorKey: 'actions',
    header: 'Actions'
  }
];

export const FeeWaiverHistoryColumnDefs: ColDefsType[] = [
  {
    headerName: 'Fee type',
    field: 'feeType',
    cellRenderer: (params: ICellRendererParams) => {
      const value = getSanitizedEnumValue(WaiveFeeType, params?.value);
      return (
        <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
          <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
            <EllipsisText text={value} />
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper">{value}</TooltipContent>
        </Tooltip>
      );
    }
  },
  {
    headerName: 'Amount(incl. GST)',
    field: 'feeAmount',

    width: 150,
    cellRenderer: (params: ICellRendererParams) => {
      const value = formatAmount(params.data?.deferredAmount, false) || '--';
      return <EllipsisText text={value} />;
    }
  },
  {
    headerName: 'Action',
    field: 'actionType',
    width: 150,
    cellRenderer: (params: ICellRendererParams) => {
      const value = getSanitizedEnumValue(WAIVE_FEE_ACTIONS, params?.value);
      return <EllipsisText text={value} />;
    }
  },
  {
    headerName: 'Deferred',
    width: 130,
    cellRenderer: (params: ICellRendererParams) => {
      const value = formatAmount(params.data?.deferredAmount, false) || '--';
      return <EllipsisText text={value} />;
    }
  },
  {
    headerName: 'Waived',
    width: 150,
    cellRenderer: (params: ICellRendererParams) => {
      const value = formatAmount(params.data?.waivedAmount, false) || '--';
      return <EllipsisText text={value} />;
    }
  },
  {
    field: 'actionDate',
    headerName: 'D.O.A',
    cellRenderer: (params: ICellRendererParams) => {
      const value = formatDate(params.value, {
        year: 'numeric'
      });
      return <EllipsisText text={value} />;
    }
  }
];
export const STATUS = {
  PAID: 'PAID',
  UNPAID: 'UNPAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  FORECLOSED: 'FORECLOSED',
  PART_PRE_PAYMENT: 'PART_PRE_PAYMENT'
};

export const ROW_WITH_GREATER_HEIGHT = [STATUS.PARTIALLY_PAID, STATUS.PAID, STATUS.UNPAID];

export const ChipObjMap = {
  ALL_EMI: {
    label: "All EMI's",
    value: 'allEmi',
    count: 0
  },
  OVERDUE: {
    label: 'Overdue',
    value: 'overdue',
    count: 0
  },
  PAID: {
    label: 'Paid',
    value: 'paid',
    count: 0
  },
  SCHEDULED: {
    label: 'Scheduled',
    value: 'scheduled',
    count: 0
  },
  FORECLOSED: {
    label: 'Foreclosed',
    value: 'FORECLOSED',
    count: 0
  },
  PARTIALLY_PAID: {
    label: 'Partially Paid',
    value: 'PARTIALLY_PAID',
    count: 0
  }
};

export enum enachMode {
  sms
}

export enum EnachSetupKinds {
  INITIAL_STATE,
  // UPDATE_THROUGH_WHATSAPP,
  UPDATE_THROUGH_SMS,
  UPDATE_PHONE_NUMBER,
  CLOSE,
  BEFORE_API_CALL,
  API_CALL_SUCCESS
}

export enum OverdueMappings {
  interest = 'Interest',
  principal = 'Principal',
  lateFee = 'Late Fee',
  bounceFee = 'Bounce Fee',
  penalInterest = 'Penal Interest'
}

export enum CalculateOverdueKind {
  INITIAL_STATE,
  SET_DATE,
  CLOSE,
  BEFORE_API_CALL,
  API_ERROR,
  API_CALL_SUCCESS,
  INPUT_DATE_ERROR
}

export const WaiveFeeActions = {
  WAIVER: { name: 'WAIVER', templateName: 'HOLD_FEE_TEMPLATE_V1' },
  HOLD: { name: 'HOLD', templateName: 'DEFER_FEE_TEMPLATE_V1' }
} as { [key: string]: { name: string; templateName: string } };

export const WAIVE_FEE_OPTIONS = [
  { value: 'HOLD', label: 'Hold' },
  { value: 'WAIVER', label: 'Waiver' }
] as SelectPickerOptionProps[];

export enum WAIVE_FEE_ACTIONS {
  HOLD = 'Hold',
  WAIVER = 'Waiver',
  DEFERMENT = 'Deferment'
}

export const WAIVE_FEE_DURATION = [{ value: '1 Day', label: '1 Day' }] as SelectPickerOptionProps[];
